import React, { useEffect, useRef, useState } from 'react';
import { Zap, Rocket, Shield } from 'lucide-react';

const metrics = [
  { label: 'Projects shipped', value: 28, icon: Rocket },
  { label: 'Avg. perf uplift', value: 42, suffix: '%', icon: Zap },
  { label: 'Deploys without rollback', value: 180, icon: Shield },
];

const CounterCard = ({ metric, visible }) => {
  const [count, setCount] = useState(0);
  const duration = 1200;

  useEffect(() => {
    if (!visible) return;
    let start = null;
    const target = metric.value;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [visible, metric.value]);

  const Icon = metric.icon;

  return (
    <div className="glass rounded-2xl border border-orange-500/20 p-6 flex items-center gap-4 hover:border-orange-500/40 transition-all duration-300 hover:-translate-y-1">
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/20 to-amber-500/20 flex items-center justify-center border border-orange-500/30">
        <Icon className="text-orange-400" size={22} />
      </div>
      <div className="flex-1">
        <div className="text-3xl font-bold text-white leading-tight">
          {count}
          {metric.suffix || ''}
        </div>
        <p className="text-sm text-gray-400">{metric.label}</p>
        <div className="mt-2 h-1 bg-zinc-900 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-orange-500 to-amber-500 transition-[width] duration-300"
            style={{ width: `${visible ? 100 : 0}%` }}
          />
        </div>
      </div>
    </div>
  );
};

const ImpactCounters = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
          }
        });
      },
      { threshold: 0.4 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="impact" data-story-section className="py-12 sm:py-16 bg-zinc-950/60 backdrop-blur relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,77,109,0.1),transparent_40%),radial-gradient(circle_at_80%_30%,rgba(110,231,255,0.08),transparent_35%)]"></div>
      <div ref={ref} className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {metrics.map((metric) => (
            <CounterCard key={metric.label} metric={metric} visible={visible} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ImpactCounters;
